import { Product, UserProfile, AnalysisSession, ChatMessage, Notification, Agent } from '@/types';

export const mockProducts: Product[] = [
  {
    id: 'prod_001',
    name: 'Gentle Hydrating Cleanser',
    price: 285,
    description: 'A pH-balanced, sulfate-free cleanser that removes impurities while maintaining skin barrier integrity. Formulated with ceramides and hyaluronic acid.',
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
    category: 'cleanser',
    concerns: ['dryness', 'sensitivity', 'barrier_repair'],
    skinTypes: ['dry', 'sensitive', 'normal', 'combination'],
    routineStep: 'cleanser',
    keyIngredients: ['Ceramides', 'Hyaluronic Acid', 'Glycerin', 'Panthenol'],
    benefits: ['Gentle cleansing', 'Hydration', 'Barrier support', 'Non-stripping'],
    usage: 'Use AM and PM. Massage onto damp skin for 60 seconds, rinse with lukewarm water.',
    cautions: ['Avoid contact with eyes', 'If irritation occurs, discontinue use'],
    pregnancySafe: true,
    layeringRules: {
      combineWith: ['all_products'],
      avoid: []
    }
  },
  {
    id: 'prod_002',
    name: 'Niacinamide 10% + Zinc 1% Serum',
    price: 395,
    description: 'High-strength niacinamide serum for oil control, pore refinement, and barrier strengthening. Zinc PCA helps regulate sebum production.',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=400&fit=crop',
    category: 'serum',
    concerns: ['acne', 'oiliness', 'enlarged_pores', 'uneven_texture'],
    skinTypes: ['oily', 'combination', 'normal'],
    routineStep: 'serum',
    keyIngredients: ['Niacinamide 10%', 'Zinc PCA 1%', 'Panthenol'],
    benefits: ['Oil control', 'Pore minimization', 'Barrier repair', 'Reduces inflammation'],
    usage: 'Apply AM and/or PM to clean, dry skin. Allow to absorb before next step.',
    cautions: ['Patch test recommended', 'May cause initial flushing', 'Do not combine with high-strength Vitamin C at same time'],
    pregnancySafe: true,
    layeringRules: {
      combineWith: ['moisturizer', 'SPF', 'hyaluronic_acid'],
      avoid: ['high_strength_vitamin_c_same_time']
    }
  },
  {
    id: 'prod_003',
    name: 'Retinol 0.3% in Squalane',
    price: 475,
    description: 'Beginner-friendly retinol serum in squalane oil base. Promotes cell turnover, reduces fine lines, and improves skin texture.',
    image: 'https://images.unsplash.com/photo-1570194065650-d99fb4bedf0a?w=400&h=400&fit=crop',
    category: 'treatment',
    concerns: ['aging', 'fine_lines', 'uneven_texture', 'hyperpigmentation'],
    skinTypes: ['normal', 'oily', 'combination'],
    routineStep: 'treatment',
    keyIngredients: ['Retinol 0.3%', 'Squalane', 'Tocopherol'],
    benefits: ['Anti-aging', 'Texture improvement', 'Brightening', 'Collagen support'],
    usage: 'PM only. Apply 2-3 times per week initially, gradually increase frequency.',
    cautions: ['Not for use during pregnancy or breastfeeding', 'Introduce slowly', 'Use SPF 30+ daily', 'Avoid eye area'],
    pregnancySafe: false,
    layeringRules: {
      combineWith: ['moisturizer', 'hyaluronic_acid'],
      avoid: ['aha', 'bha', 'vitamin_c_same_time', 'benzoyl_peroxide']
    }
  },
  {
    id: 'prod_004',
    name: 'Broad Spectrum SPF 50+',
    price: 325,
    description: 'Lightweight, non-comedogenic sunscreen with broad spectrum UVA/UVB protection. Contains antioxidants and hydrating ingredients.',
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
    category: 'SPF',
    concerns: ['sun_protection', 'photoaging', 'hyperpigmentation'],
    skinTypes: ['all'],
    routineStep: 'SPF',
    keyIngredients: ['Zinc Oxide', 'Octinoxate', 'Niacinamide', 'Vitamin E'],
    benefits: ['Broad spectrum protection', 'Non-greasy', 'No white cast', 'Antioxidant protection'],
    usage: 'Apply AM as final step. Use 1/4 teaspoon for face and neck. Reapply every 2 hours when exposed to sun.',
    cautions: ['Reapply after swimming or sweating'],
    pregnancySafe: true,
    layeringRules: {
      combineWith: ['all_products'],
      avoid: []
    }
  },
  {
    id: 'prod_005',
    name: 'Hyaluronic Acid 2% + B5',
    price: 345,
    description: 'Multi-weight hyaluronic acid serum with Vitamin B5 for deep hydration and surface moisture retention.',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=400&fit=crop',
    category: 'serum',
    concerns: ['dehydration', 'dryness', 'fine_lines', 'sensitivity'],
    skinTypes: ['all'],
    routineStep: 'serum',
    keyIngredients: ['Hyaluronic Acid 2%', 'Panthenol (B5)', 'Glycerin'],
    benefits: ['Intense hydration', 'Plumping', 'Soothing', 'Improves elasticity'],
    usage: 'Apply AM and/or PM to damp skin. Follow with moisturizer to seal in hydration.',
    cautions: ['Apply to damp skin for best results'],
    pregnancySafe: true,
    layeringRules: {
      combineWith: ['all_products'],
      avoid: []
    }
  }
];

export const mockUserProfile: UserProfile = {
  id: 'user_123',
  name: 'Sarah Johnson',
  email: 'sarah.johnson@example.com',
  environment: 'urban_high_pollution',
  lifestyle: 'active_outdoors',
  allergies: ['fragrance', 'essential_oils'],
  sensitivities: ['high_concentration_acids', 'alcohol'],
  goals: ['reduce_acne', 'even_skin_tone', 'anti_aging'],
  budget: 'moderate',
  skinType: 'combination',
  ageRange: '25-34'
};

export const mockAnalysisSession: AnalysisSession = {
  id: 'analysis_456',
  status: 'completed',
  questionnaire: {
    skinType: 'combination',
    concerns: ['acne', 'hyperpigmentation', 'fine_lines'],
    sensitivity: 'moderate',
    currentRoutine: 'basic_cleanser_moisturizer',
    triggers: ['hormonal_changes', 'stress', 'dairy']
  },
  images: {
    front: 'analysis_456_front.jpg',
    left: 'analysis_456_left.jpg',
    right: 'analysis_456_right.jpg'
  },
  result: {
    findings: [
      {
        category: 'acne_mild_moderate',
        description: 'Mild to moderate acne present on cheeks and jawline, primarily inflammatory papules.',
        confidence: 0.85
      },
      {
        category: 'post_inflammatory_hyperpigmentation',
        description: 'Post-inflammatory hyperpigmentation visible on cheeks and chin from previous breakouts.',
        confidence: 0.92
      },
      {
        category: 'early_signs_aging',
        description: 'Fine lines beginning to form on forehead and around eyes.',
        confidence: 0.78
      }
    ],
    recommendations: [
      {
        category: 'gentle_exfoliation',
        priority: 'high',
        explanation: 'Introduce salicylic acid (BHA) to unclog pores and reduce inflammation.'
      },
      {
        category: 'brightening_treatment',
        priority: 'high',
        explanation: 'Use niacinamide and vitamin C to fade hyperpigmentation and even skin tone.'
      },
      {
        category: 'retinoid_introduction',
        priority: 'medium',
        explanation: 'Consider low-strength retinol for anti-aging benefits and acne prevention.'
      }
    ],
    regimen: {
      id: 'regimen_789',
      name: 'Acne & Brightening Regimen',
      amRoutine: [
        {
          id: 'step_1',
          stepNumber: 1,
          productId: 'prod_001',
          productName: 'Gentle Hydrating Cleanser',
          instruction: 'Cleanse face with lukewarm water for 60 seconds',
          frequency: 'Daily AM'
        },
        {
          id: 'step_2',
          stepNumber: 2,
          productId: 'prod_002',
          productName: 'Niacinamide 10% + Zinc 1% Serum',
          instruction: 'Apply 2-3 drops to entire face, avoid eye area',
          frequency: 'Daily AM'
        },
        {
          id: 'step_3',
          stepNumber: 3,
          productId: 'prod_004',
          productName: 'Broad Spectrum SPF 50+',
          instruction: 'Apply 1/4 teaspoon to face and neck as final step',
          frequency: 'Daily AM'
        }
      ],
      pmRoutine: [
        {
          id: 'step_4',
          stepNumber: 1,
          productId: 'prod_001',
          productName: 'Gentle Hydrating Cleanser',
          instruction: 'Double cleanse if wearing sunscreen/makeup',
          frequency: 'Daily PM'
        },
        {
          id: 'step_5',
          stepNumber: 2,
          productId: 'prod_005',
          productName: 'Hyaluronic Acid 2% + B5',
          instruction: 'Apply to damp skin, wait 30 seconds to absorb',
          frequency: 'Daily PM'
        },
        {
          id: 'step_6',
          stepNumber: 3,
          productId: 'prod_003',
          productName: 'Retinol 0.3% in Squalane',
          instruction: 'Apply 2-3 times per week, avoid eye area',
          frequency: '2-3x per week PM'
        }
      ],
      weeklyTreatments: [
        {
          id: 'step_7',
          stepNumber: 1,
          productName: 'Clay Mask',
          instruction: 'Apply to oily areas only, leave for 10 minutes',
          frequency: '1x per week',
          duration: '10 minutes'
        }
      ],
      durationWeeks: 12,
      notes: 'Introduce retinol slowly. Expect purging for 4-6 weeks. Always use SPF during the day.'
    },
    suggestedProducts: ['prod_001', 'prod_002', 'prod_003', 'prod_004', 'prod_005']
  },
  createdAt: '2024-01-15T10:30:00Z',
  completedAt: '2024-01-15T11:45:00Z'
};

export const mockLeroyResponses = [
  "Hi! I'm Leroy, your AI skincare advisor. I can help you with product recommendations, ingredient explanations, and building your personalized regimen. How can I assist you today?",
  "Based on your skin profile, I'd recommend starting with a gentle cleanser and niacinamide serum for acne control. Would you like me to explain why these would work well for you?",
  "Niacinamide is excellent for combination skin because it regulates oil production while strengthening your skin barrier. It's also well-tolerated by most people.",
  "For your hyperpigmentation concerns, I'd suggest adding vitamin C in the morning and being diligent with SPF 50+. Consistency is key for fading dark spots.",
  "I notice you have fragrance allergies. All Marple products are fragrance-free, so you're safe with our entire range!",
  "Your skin analysis shows mild acne and early signs of aging. A retinol would address both concerns, but we should introduce it slowly to minimize irritation.",
  "Would you like me to create a complete AM/PM routine based on your analysis results? I can map each step to specific Marple products."
];

export const mockChatMessages: ChatMessage[] = [
  {
    id: 'msg_1',
    role: 'assistant',
    content: mockLeroyResponses[0],
    timestamp: '2024-01-15T10:00:00Z'
  },
  {
    id: 'msg_2',
    role: 'user',
    content: 'What products would you recommend for my combination skin with acne?',
    timestamp: '2024-01-15T10:01:00Z'
  },
  {
    id: 'msg_3',
    role: 'assistant',
    content: mockLeroyResponses[1],
    timestamp: '2024-01-15T10:01:05Z'
  }
];

export const mockNotifications: Notification[] = [
  {
    id: 'notif_1',
    userId: 'user_123',
    type: 'analysis_complete',
    title: 'Skin Analysis Complete',
    message: 'Your personalized report and regimen are ready!',
    read: false,
    createdAt: '2024-01-15T11:45:00Z',
    actionUrl: '/regimen'
  },
  {
    id: 'notif_2',
    userId: 'user_123',
    type: 'regimen_reminder',
    title: 'PM Routine Reminder',
    message: 'Time for your evening skincare routine!',
    read: true,
    createdAt: '2024-01-15T19:00:00Z',
    actionUrl: '/regimen'
  },
  {
    id: 'notif_3',
    userId: 'user_123',
    type: 'order_update',
    title: 'Order Shipped',
    message: 'Your Marple order #12345 has been shipped.',
    read: true,
    createdAt: '2024-01-14T14:30:00Z',
    actionUrl: '/orders'
  }
];

export const mockAgent: Agent = {
  id: 'agent_789',
  name: 'Jessica Martinez',
  email: 'jessica.martinez@example.com',
  rank: 'Silver',
  sponsorId: 'agent_456',
  referralCode: 'JESSICA25',
  joinDate: '2023-06-15T00:00:00Z'
};

export const skinConcerns = [
  { id: 'acne', label: 'Acne & Breakouts', icon: '🔴' },
  { id: 'hyperpigmentation', label: 'Dark Spots & Pigmentation', icon: '🟤' },
  { id: 'aging', label: 'Fine Lines & Wrinkles', icon: '✨' },
  { id: 'dryness', label: 'Dryness & Dehydration', icon: '💧' },
  { id: 'sensitivity', label: 'Sensitivity & Redness', icon: '🌸' },
  { id: 'oiliness', label: 'Oil Control', icon: '🛡️' },
  { id: 'pores', label: 'Large Pores', icon: '🔍' },
  { id: 'dullness', label: 'Dullness', icon: '💎' }
];

export const skinTypes = [
  { id: 'dry', label: 'Dry' },
  { id: 'oily', label: 'Oily' },
  { id: 'combination', label: 'Combination' },
  { id: 'normal', label: 'Normal' },
  { id: 'sensitive', label: 'Sensitive' }
];

export const environments = [
  { id: 'urban_high_pollution', label: 'Urban/High Pollution' },
  { id: 'dry_climate', label: 'Dry Climate' },
  { id: 'humid_climate', label: 'Humid Climate' },
  { id: 'cold_climate', label: 'Cold Climate' },
  { id: 'temperate', label: 'Temperate' }
];

export const budgets = [
  { id: 'minimal', label: 'Minimal (< R300/month)' },
  { id: 'moderate', label: 'Moderate (R300-600/month)' },
  { id: 'premium', label: 'Premium (R600-1000/month)' },
  { id: 'luxury', label: 'Luxury (> R1000/month)' }
];